import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Mail, Phone } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contacts = [
  {
    name: 'Peter Dilling',
    role: 'Direktør',
    email: 'peter@dilling-el.dk',
    phone: '+45 46 46 12 34',
  },
  {
    name: 'Caspar Clemmensen',
    role: 'Projektleder',
    email: 'caspar@dilling-el.dk',
    phone: '+45 46 46 12 34',
  },
];

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        building_type: '',
      });
      setIsSubmitted(true);
      toast.success('Tak for din henvendelse');
      
      // Track successful form submission in GA4
      if (typeof window.gtag === 'function') {
        // Send generate_lead event with GA4 recommended parameters
        window.gtag('event', 'generate_lead', {
          currency: 'DKK',
          value: 1
        });
      }
    } catch (error) {
      toast.error('Der opstod en fejl');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <main data-testid="contact-page" className="pt-24">
      <SEOHead path="/kontakt" />
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#F4F2EE]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
              Kontakt
            </span>
            <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-medium text-[#0E3D24] mt-6 leading-[1.1]">
              Lad os tale om jeres projekt
            </h1>
            <p className="text-[#1E2A24]/70 text-lg mt-6 leading-relaxed">
              Vi starter altid med en uforpligtende samtale om jeres bygning og behov.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Persons */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
              Kontaktpersoner
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-[#0E3D24] mt-6">
              Tal direkte med os
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
            {contacts.map((contact, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-6"
              >
                {/* Photo */}
                <div className="w-24 h-24 bg-[#A6B8A2]/20 flex-shrink-0 overflow-hidden">
                  <img loading="lazy" 
                    src={index === 0 ? '/images/peter-dilling.webp' : '/images/caspar-clemmensen.webp'} 
                    alt={contact.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Info */}
                <div>
                  <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#1E2A24]">
                    {contact.name}
                  </h3>
                  <p className="text-[#A6B8A2] text-sm mb-4">{contact.role}</p>
                  
                  <div className="space-y-2">
                    <a 
                      href={`mailto:${contact.email}`}
                      className="flex items-center gap-2 text-sm text-[#1E2A24]/70 hover:text-[#0E3D24] transition-colors"
                    >
                      <Mail size={14} />
                      <span>{contact.email}</span>
                    </a>
                    <a 
                      href={`tel:${contact.phone.replace(/\s/g, '')}`}
                      className="flex items-center gap-2 text-sm text-[#1E2A24]/70 hover:text-[#0E3D24] transition-colors"
                    >
                      <Phone size={14} />
                      <span>{contact.phone}</span>
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24 md:py-32 bg-[#F4F2EE]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left - Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
                Skriv til os
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-[#0E3D24] mt-6 mb-12">
                Send en besked
              </h2>

              <div className="space-y-6">
                <div>
                  <p className="text-[11px] tracking-[0.15em] uppercase text-[#A6B8A2] mb-2">Email</p>
                  <a 
                    href="mailto:info@dilling-el.dk" 
                    className="text-[#1E2A24] hover:text-[#1F5A3A] transition-colors"
                  >
                    info@dilling-el.dk
                  </a>
                </div>
                <div>
                  <p className="text-[11px] tracking-[0.15em] uppercase text-[#A6B8A2] mb-2">Telefon</p>
                  <a 
                    href="tel:+4546461234" 
                    className="text-[#1E2A24] hover:text-[#1F5A3A] transition-colors"
                  >
                    +45 46 46 12 34
                  </a>
                </div>
                <div>
                  <p className="text-[11px] tracking-[0.15em] uppercase text-[#A6B8A2] mb-2">Adresse</p>
                  <p className="text-[#1E2A24]">
                    Gammel Darupvej 5D<br />
                    4000 Roskilde
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              {isSubmitted ? (
                <div className="flex flex-col items-start">
                  <div className="w-12 h-12 rounded-full border border-[#0E3D24] flex items-center justify-center mb-8">
                    <Check size={20} className="text-[#0E3D24]" />
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-3xl font-medium text-[#1E2A24] mb-4">
                    Tak for din henvendelse
                  </h3>
                  <p className="text-[#1E2A24]/70 mb-8">
                    Vi vender tilbage inden for 1-2 hverdage.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-[11px] tracking-[0.15em] uppercase text-[#1F5A3A] hover:text-[#0E3D24] transition-colors"
                  >
                    Send ny besked
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-[11px] tracking-[0.15em] uppercase text-[#A6B8A2] block mb-3">
                        Navn *
                      </label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="border-0 border-b border-[#A6B8A2] rounded-none px-0 py-3 focus:border-[#0E3D24] transition-colors bg-transparent text-[#1E2A24]"
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] tracking-[0.15em] uppercase text-[#A6B8A2] block mb-3">
                        Email *
                      </label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="border-0 border-b border-[#A6B8A2] rounded-none px-0 py-3 focus:border-[#0E3D24] transition-colors bg-transparent text-[#1E2A24]"
                        data-testid="contact-input-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-[11px] tracking-[0.15em] uppercase text-[#A6B8A2] block mb-3">
                        Telefon
                      </label>
                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="border-0 border-b border-[#A6B8A2] rounded-none px-0 py-3 focus:border-[#0E3D24] transition-colors bg-transparent text-[#1E2A24]"
                        data-testid="contact-input-phone"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] tracking-[0.15em] uppercase text-[#A6B8A2] block mb-3">
                        Virksomhed
                      </label>
                      <Input
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="border-0 border-b border-[#A6B8A2] rounded-none px-0 py-3 focus:border-[#0E3D24] transition-colors bg-transparent text-[#1E2A24]"
                        data-testid="contact-input-company"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] tracking-[0.15em] uppercase text-[#A6B8A2] block mb-3">
                      Besked *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="border-0 border-b border-[#A6B8A2] rounded-none px-0 py-3 focus:border-[#0E3D24] transition-colors resize-none bg-transparent text-[#1E2A24]"
                      data-testid="contact-input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-3 text-[11px] tracking-[0.15em] uppercase bg-[#0E3D24] text-white px-8 py-4 hover:bg-[#1F5A3A] transition-colors group mt-4"
                    data-testid="contact-submit-btn"
                  >
                    <span>{isSubmitting ? 'Sender...' : 'Send besked'}</span>
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
